import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Activity, Brain, Microscope, Shield, Lock } from 'lucide-react';
import { trackButtonClick, trackNavigation } from '../utils/analytics';
import EITLogo from '../assets/EIT.png';
import EITFundedLogo from '../assets/EITFUNDED.png';
import LogoPTLogo from '../assets/LogoPT.png';
import StartupPortugalLogo from '../assets/startupportugal.png';
import CI3Logo from '../assets/ci3.png';
import NvidiaLogo from '../assets/nvidia.png';
import AWSLogo from '../assets/aws.webp';

const HeroSection = () => {
  const containerRef = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 300], [0, -50]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  const scrollToDemo = () => {
    trackButtonClick('Watch Demo', 'Hero Section');
    const demoSection = document.getElementById('demo');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLearnMore = () => {
    trackButtonClick('Learn More', 'Hero Section');
    trackNavigation('Technology Section');
    document.getElementById('technology').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <HeroContainer ref={containerRef}>
      <BackgroundGradient />
      <GridPattern />
      
      <FloatingIcons>
        <FloatingIcon
          animate={{
            y: [-10, 10, -10],
            rotate: [0, 5, -5, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{ left: '10%', top: '20%' }}
        >
          <Brain size={32} />
        </FloatingIcon>
        <FloatingIcon
          animate={{
            y: [-15, 15, -15],
            rotate: [0, -5, 5, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{ right: '15%', top: '30%' }}
        >
          <Activity size={32} />
        </FloatingIcon>
        <FloatingIcon
          animate={{
            y: [-12, 12, -12],
            rotate: [0, 5, -5, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{ left: '20%', bottom: '30%' }}
        >
          <Microscope size={32} />
        </FloatingIcon>
      </FloatingIcons>

      <ContentWrapper style={{ y, opacity }}>
        <Content>
          <CertificationBubbles>
            <CertificationBubble
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Shield size={16} /> HL7 FHIR Ready
            </CertificationBubble>
            <CertificationBubble
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Lock size={16} /> GDPR Compliant
            </CertificationBubble>
          </CertificationBubbles>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Title>
              The Right
              <GradientSpan> Cancer </GradientSpan>
              Therapy
            </Title>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Description>
              We support oncologists with <strong>evidence-based</strong> insights to improve late-stage cancer care, accelerating research and turning discovery into better outcomes.
            </Description>
          </motion.div>
          <ButtonGroup>
            <PrimaryButton
              as={motion.button}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={scrollToDemo}
            >
              Watch Demo <ArrowRight size={20} />
            </PrimaryButton>
            <SecondaryButton
              as={motion.button}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleLearnMore}
            >
              Learn More
            </SecondaryButton>
          </ButtonGroup>

          <PartnersSection>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <PartnersTitle>Partnering with Leading Institutions</PartnersTitle>
              <PartnersCarousel>
                <CarouselTrack>
                  {[...Array(2)].map((_, setIndex) => (
                    <CarouselSet key={setIndex}>
                      <PartnerLogo>
                        <LogoImage src={EITLogo} alt="EIT Health" />
                      </PartnerLogo>
                      <PartnerLogo>
                        <LogoImage src={EITFundedLogo} alt="EIT Funded" />
                      </PartnerLogo>
                      <PartnerLogo>
                        <LogoImage src={LogoPTLogo} alt="Logo PT" />
                      </PartnerLogo>
                      <PartnerLogo>
                        <LogoImage src={StartupPortugalLogo} alt="Startup Portugal" />
                      </PartnerLogo>
                      <PartnerLogo>
                        <LogoImage src={CI3Logo} alt="CI3" />
                      </PartnerLogo>
                      <PartnerLogo>
                        <LogoImage src={NvidiaLogo} alt="NVIDIA" />
                      </PartnerLogo>
                      <PartnerLogo>
                        <LogoImage src={AWSLogo} alt="AWS" />
                      </PartnerLogo>
                    </CarouselSet>
                  ))}
                </CarouselTrack>
              </PartnersCarousel>
            </motion.div>
          </PartnersSection>
        </Content>
      </ContentWrapper>
    </HeroContainer>
  );
};

const HeroContainer = styled.section`
  min-height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  background: #f8fafc;
`;

const BackgroundGradient = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.1) 0%,
    rgba(147, 51, 234, 0.1) 100%
  );
  z-index: 1;
`;

const GridPattern = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
  z-index: 2;
`;

const FloatingIcons = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 3;
`;

const FloatingIcon = styled(motion.div)`
  position: absolute;
  color: rgba(59, 130, 246, 0.6);
  filter: drop-shadow(0 0 10px rgba(59, 130, 246, 0.3));
`;

const ContentWrapper = styled(motion.div)`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 4;
  padding: 0 2rem;
`;

const Content = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  text-align: center;
  padding-top: 4rem;
`;

const CertificationBubbles = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }
`;

const CertificationBubble = styled(motion.div)`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 25px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #4a5568;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 6px;
  
  svg {
    color: #3b82f6;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 6px 12px;
    gap: 4px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const Title = styled.h1`
  font-size: 4.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: #1a1a1a;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const GradientSpan = styled.span`
  background: linear-gradient(135deg, #2094F1 0%, #AF54AE 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Description = styled.p`
  font-size: 1.25rem;
  line-height: 1.5;
  color: #4a5568;
  margin-bottom: 2.5rem;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    max-width: 95%;
    line-height: 1.4;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const BaseButton = styled(motion.button)`
  padding: 1rem 2rem;
  font-size: 1.125rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const PrimaryButton = styled(BaseButton)`
  background: linear-gradient(135deg, #2094F1 0%, #AF54AE 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 6px rgba(32, 148, 241, 0.2);

  &:hover {
    box-shadow: 0 6px 8px rgba(32, 148, 241, 0.3);
  }
`;

const SecondaryButton = styled(BaseButton)`
  background: transparent;
  color: #2094F1;
  border: 2px solid #2094F1;

  &:hover {
    background: rgba(59, 130, 246, 0.1);
  }
`;

const PartnersSection = styled.div`
  margin-top: 60px;
  width: 100%;
`;

const PartnersTitle = styled.h3`
  font-size: 1.2rem;
  color: #666;
  text-align: center;
  margin-bottom: 30px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const PartnersCarousel = styled.div`
  width: 100%;
  overflow: hidden;
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 10%,
    black 90%,
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 10%,
    black 90%,
    transparent 100%
  );
`;

const CarouselTrack = styled.div`
  display: flex;
  animation: scroll 30s linear infinite;
  width: calc(200% + 40px);

  @keyframes scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }
`;

const CarouselSet = styled.div`
  display: flex;
  min-width: 50%;
  justify-content: space-around;
  align-items: center;
  flex-shrink: 0;
  gap: 60px;
  padding: 0 20px;
`;

const PartnerLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  min-width: 140px;
  opacity: 0.6;
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 1;
  }
`;

const LogoPlaceholder = styled.div`
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  color: #64748b;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  border: 2px solid #f1f5f9;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const LogoImage = styled.img`
  height: 50px;
  max-width: 140px;
  width: auto;
  object-fit: contain;
  filter: grayscale(100%) opacity(0.6);
  transition: all 0.3s ease;
  
  ${PartnerLogo}:hover & {
    filter: grayscale(0%) opacity(1);
  }
`;



export default HeroSection;